/**
 * Schedules API Client (Story 7.2 — Report Delivery & Scheduling)
 *
 * Typed wrapper around apiGet/apiPost/apiPatch/apiDelete for the 4 report-schedule
 * endpoints. Mirrors the server projection shape from
 * apps/server/src/routes/schedules/crud.ts (the `{ data }` envelope).
 *
 *   GET    /api/schedules?clientId=<id> → { data: { schedules: ScheduleProjection[] } }
 *   POST   /api/schedules               → 201 { data: { schedule, recipients } }
 *   PATCH  /api/schedules/:id           → { data: { schedule } }
 *   DELETE /api/schedules/:id           → 204
 *
 * 🔴 apps/web CANNOT depend on @provenleap/db (rootDir constraint — see
 * widgetDataForms.tsx / goalPageDisplay.ts). The `Frequency`/`FREQUENCY_VALUES`/
 * `LAST_DAY_OF_MONTH` exports the story asks for therefore CANNOT be imported
 * from @provenleap/db here; they are duplicated locally and MUST stay
 * byte-identical to 7.1's schema/report-scheduling.ts.
 * 🔴 SYNC: packages/db/src/schema/report-scheduling.ts
 */

import { apiGet, apiPost, apiPatch, apiDelete } from "./apiClient";

// ── Local mirror of @provenleap/db enums/sentinels (SYNC: report-scheduling.ts) ──

/** The four delivery cadences. Mirrors db FREQUENCY_VALUES. */
export const FREQUENCY_VALUES = [
  "daily",
  "weekly",
  "biweekly",
  "monthly",
] as const;
export type Frequency = (typeof FREQUENCY_VALUES)[number];

/** day_of_month sentinel for "last day of the month". Mirrors db LAST_DAY_OF_MONTH. */
export const LAST_DAY_OF_MONTH = -1;

// ── Projections (mirror apps/server/src/routes/schedules/crud.ts) ──

export interface ScheduleProjection {
  id: string;
  clientId: string;
  templateId: string;
  templateName: string | null;
  frequency: Frequency;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  timeUtc: string;
  timezone: string;
  /** Server-computed next delivery — epoch ms. */
  nextRunAt: number;
  isActive: boolean;
  recipientCount: number;
  /** Story 7.9 — per-schedule open/click tracking opt-in (OFF by default). */
  trackingEnabled: boolean;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

/** The 6 delivery-lifecycle statuses (mirror db SCHEDULED_REPORT_LOG_STATUS_VALUES). */
export type DeliveryStatus =
  | "pending"
  | "generating"
  | "sending"
  | "delivered"
  | "failed"
  | "bounced";

/** A delivery-history log row (mirror apps/server/src/routes/schedules/logs.ts). */
export interface DeliveryLog {
  id: string;
  /** When the run was triggered — epoch ms. */
  triggeredAt: number;
  status: DeliveryStatus;
  errorMessage: string | null;
  /** When the delivery email was dispatched — epoch ms, or null. */
  emailSentAt: number | null;
  reportId: string | null;
  /** Story 7.9 — open/click counts (0 when tracking off / not yet recorded). */
  openCount: number;
  clickCount: number;
  /** First open — epoch ms, or null. */
  openedAt: number | null;
  recipientCount: number;
}

export interface RecipientProjection {
  id: string;
  email: string;
  name: string | null;
  isPrimary: boolean;
  createdAt: number;
}

/** A recipient row + its per-row suppression state (GET /:id/recipients, Story 7.8). */
export type SuppressionReason = "user_unsubscribe" | "bounce" | "complaint";

export interface RecipientWithSuppression extends RecipientProjection {
  suppressed: boolean;
  suppressionReason: SuppressionReason | null;
}

export interface ScheduleRecipientInput {
  email: string;
  name?: string;
  isPrimary?: boolean;
}

export interface CreateScheduleInput {
  clientId: string;
  templateId: string;
  frequency: Frequency;
  /** Required iff weekly/biweekly (0..6, Sun..Sat); null/omitted otherwise. */
  dayOfWeek?: number | null;
  /** Required iff monthly (1..28 or LAST_DAY_OF_MONTH); null/omitted otherwise. */
  dayOfMonth?: number | null;
  /** "HH:MM" UTC. */
  timeUtc: string;
  /** IANA timezone (display hint). */
  timezone: string;
  /** 1..10 recipients (Story 7.8 multi-recipient editor); exactly one Primary. */
  recipients: ScheduleRecipientInput[];
  /** Story 7.9 — opt into open/click tracking (OFF by default; omitted ⇒ false). */
  trackingEnabled?: boolean;
}

export interface UpdateScheduleInput {
  frequency?: Frequency;
  dayOfWeek?: number | null;
  dayOfMonth?: number | null;
  timeUtc?: string;
  timezone?: string;
  isActive?: boolean;
  templateId?: string;
  /** Story 7.9 — toggle open/click tracking on/off. */
  trackingEnabled?: boolean;
  /**
   * Story 7.8 — OPTIONAL recipient REPLACE-SET. When present the server atomically
   * swaps the schedule's entire recipient set (delete-all + insert-new). When omitted,
   * recipients are untouched (cadence-only patch).
   */
  recipients?: ScheduleRecipientInput[];
}

/**
 * Cadence-only input for the 7.4 preview endpoint (no client/template/recipients —
 * the preview is a pure calculation). `timeUtc` is the UTC anchor (the modal
 * converts the picked local time → UTC before calling).
 */
export interface PreviewScheduleInput {
  frequency: Frequency;
  dayOfWeek?: number | null;
  dayOfMonth?: number | null;
  timeUtc: string;
  timezone: string;
}

interface ApiError {
  type?: string;
  title?: string;
  detail?: string;
  status?: number;
  errors?: Array<{ field: string; message: string }>;
}

async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as ApiError;
    const message = err.detail ?? err.title ?? `Request failed (${res.status})`;
    const error = new Error(message) as Error & {
      status?: number;
      apiError?: ApiError;
    };
    error.status = res.status;
    error.apiError = err;
    throw error;
  }
  const json = (await res.json()) as { data: T };
  return json.data;
}

/** GET /api/schedules?clientId=<id> — the client's schedules, newest first. */
export async function listSchedules(
  clientId: string
): Promise<ScheduleProjection[]> {
  const res = await apiGet(
    `/api/schedules?clientId=${encodeURIComponent(clientId)}`
  );
  const { schedules } = await unwrap<{ schedules: ScheduleProjection[] }>(res);
  return schedules;
}

/** POST /api/schedules — create a schedule + its recipient row(s). */
export async function createSchedule(
  input: CreateScheduleInput
): Promise<{ schedule: ScheduleProjection; recipients: RecipientProjection[] }> {
  const res = await apiPost(`/api/schedules`, input);
  return unwrap<{
    schedule: ScheduleProjection;
    recipients: RecipientProjection[];
  }>(res);
}

/**
 * GET /api/schedules/:id/recipients — the schedule's recipients + per-row
 * suppression state (Story 7.8). Used by the edit-mode recipient editor to seed the
 * list and badge unsubscribed/bounced rows.
 */
export async function getScheduleRecipients(
  id: string
): Promise<RecipientWithSuppression[]> {
  const res = await apiGet(`/api/schedules/${id}/recipients`);
  const { recipients } = await unwrap<{
    recipients: RecipientWithSuppression[];
  }>(res);
  return recipients;
}

/** PATCH /api/schedules/:id — partial edit; server recomputes nextRunAt. */
export async function updateSchedule(
  id: string,
  input: UpdateScheduleInput
): Promise<ScheduleProjection> {
  const res = await apiPatch(`/api/schedules/${id}`, input);
  const { schedule } = await unwrap<{ schedule: ScheduleProjection }>(res);
  return schedule;
}

/**
 * POST /api/schedules/preview — the next N occurrences (epoch ms) for the supplied
 * cadence (Story 7.4). Pure server computation (auth-only, no DB) folding the same
 * `computeNextRun` the cron uses — the web NEVER reimplements the schedule math, it
 * only formats these instants. Returns the occurrences array (default 4 = next + 3).
 */
export async function previewSchedule(
  input: PreviewScheduleInput
): Promise<number[]> {
  const res = await apiPost(`/api/schedules/preview`, input);
  const { occurrences } = await unwrap<{ occurrences: number[] }>(res);
  return occurrences;
}

/**
 * GET /api/schedules/:id/logs — the schedule's delivery history (Story 7.9),
 * newest-first, paginated. Viewer-readable. Each row carries status + recipient
 * count + (when tracking on) open/click counts.
 */
export async function getScheduleLogs(
  scheduleId: string,
  opts?: { limit?: number; offset?: number }
): Promise<DeliveryLog[]> {
  const params = new URLSearchParams();
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
  const qs = params.toString();
  const res = await apiGet(
    `/api/schedules/${scheduleId}/logs${qs ? `?${qs}` : ""}`
  );
  const { logs } = await unwrap<{ logs: DeliveryLog[] }>(res);
  return logs;
}

/**
 * POST /api/schedules/:id/logs/:logId/retry — re-attempt a failed/bounced run
 * (Story 7.9; MANAGE_REPORTS). The server resets the log to pending + re-enqueues
 * the SAME run (true redelivery). 409 if the log is not failed/bounced.
 */
export async function retryScheduleLog(
  scheduleId: string,
  logId: string
): Promise<{ id: string; status: string }> {
  const res = await apiPost(
    `/api/schedules/${scheduleId}/logs/${logId}/retry`,
    {}
  );
  return unwrap<{ id: string; status: string }>(res);
}

/** DELETE /api/schedules/:id — hard delete (children CASCADE). */
export async function deleteSchedule(id: string): Promise<void> {
  const res = await apiDelete(`/api/schedules/${id}`);
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as ApiError;
    throw new Error(err.detail ?? err.title ?? "Failed to delete schedule");
  }
}
